import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine } from "@/components/Reveal";
import { IMAGES } from "@/data/site";
import { scrollToHash } from "@/lib/scroll";
import { useLang } from "@/i18n/LanguageContext";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="hv-grain relative flex h-[100svh] min-h-[620px] items-end overflow-hidden bg-midnight"
    >
      <motion.img
        src={IMAGES.hero}
        srcSet="/images/stir/hero-900.jpg 900w, /images/stir/hero.jpg 1536w"
        sizes="100vw"
        alt={t.hero.imgAlt}
        style={{ y }}
        initial={{ scale: 1.12, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0 h-[118%] w-full object-cover object-[50%_38%]"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-black/40" />

      <p
        className="absolute right-8 top-1/2 hidden -translate-y-1/2 rotate-90 font-sans text-[10px] tracking-[0.6em] text-ivory/50 lg:block"
        data-testid="hero-side-label"
      >
        {t.hero.side}
      </p>

      <div className="relative z-10 mx-auto w-full max-w-[1680px] px-6 pb-28 lg:px-12 lg:pb-36">
        <motion.p
          data-testid="hero-eyebrow"
          className="font-sans text-[11px] tracking-[0.45em] text-gold"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="mt-5 font-serif font-light leading-[1.04] text-ivory">
          <MaskedLine delay={0.45} className="text-[clamp(2.7rem,11.5vw,4.75rem)]">
            {t.hero.line1}
          </MaskedLine>
          <MaskedLine delay={0.6} className="text-[clamp(2.7rem,11.5vw,4.75rem)]">
            <span className="italic text-gold">{t.hero.line2}</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subline"
          className="mt-7 font-sans text-sm font-light tracking-[0.18em] text-ivory/75 md:text-base"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.1, delay: 0.95 }}
        >
          {t.hero.sub}
        </motion.p>

          <motion.div
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15 }}
          >
          <button
            onClick={() => scrollToHash("#services")}
            data-testid="hero-explore-services-btn"
            className="border border-ivory/30 px-10 py-4 font-sans text-[11px] tracking-[0.3em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            {t.hero.secondary}
          </button>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-6 hidden flex-col items-center gap-3 md:flex lg:left-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        aria-hidden="true"
      >
        <span className="font-sans text-[9px] tracking-[0.5em] text-ivory/40">{t.hero.scroll}</span>
        <motion.span
          className="h-10 w-px bg-gold/60"
          animate={{ scaleY: [1, 0.4, 1] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
